import React, { useState } from "react";
import {
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import GetAppIcon from "@material-ui/icons/GetApp";

/*
 * Traceability upload issues (IError from the backend extractor):
 *   { type, sheet, row, column, msg, statusCode }
 *
 * The backend stores and returns them in several shapes, all handled here:
 *   - an array of objects (Check response `warnings`)
 *   - "Error: {...}\n{...}" (Check response `error`, upload history `stacktrace`)
 *   - "{...}\n{...}" (upload history `warnings` of a real upload)
 *   - a Postgres array literal '{"{\"type\":...}","{...}"}' (upload history
 *     `warnings` of a Check, where an array is saved into a text column)
 */

// Elements are JSON objects, so the literal always opens with {"{
const isPgArrayLiteral = (str) => str.startsWith('{"{') && str.endsWith('"}');

// Parses a Postgres text[] literal of quoted elements into its strings.
const parsePgArrayLiteral = (str) => {
  const items = [];
  let i = 1;
  while (i < str.length - 1) {
    if (str[i] !== '"') {
      i += 1;
      continue;
    }
    let item = "";
    i += 1;
    while (i < str.length && str[i] !== '"') {
      if (str[i] === "\\") i += 1;
      item += str[i];
      i += 1;
    }
    items.push(item);
    i += 1;
  }
  return items;
};

// Extracts every top-level {...} block of a string, ignoring braces inside JSON strings.
const extractJsonObjects = (str) => {
  const objects = [];
  let depth = 0;
  let start = -1;
  let inString = false;
  for (let i = 0; i < str.length; i += 1) {
    const char = str[i];
    if (inString) {
      if (char === "\\") i += 1;
      else if (char === '"') inString = false;
    } else if (char === '"' && depth > 0) {
      inString = true;
    } else if (char === "{") {
      if (depth === 0) start = i;
      depth += 1;
    } else if (char === "}" && depth > 0) {
      depth -= 1;
      if (depth === 0) {
        try {
          objects.push(JSON.parse(str.slice(start, i + 1)));
        } catch {
          /* not a JSON object, skip it */
        }
      }
    }
  }
  return objects;
};

const toIssue = (item) => {
  if (!item || typeof item !== "object" || Array.isArray(item)) return null;
  const msg = item.msg ?? item.message;
  if (typeof msg !== "string" || !msg.trim()) return null;
  return {
    msg,
    ...(item.sheet ? { sheet: String(item.sheet) } : {}),
    ...(item.row !== undefined && item.row !== null && item.row !== ""
      ? { row: String(item.row) }
      : {}),
    ...(item.column
      ? {
          column: Array.isArray(item.column)
            ? item.column.join(", ")
            : String(item.column),
        }
      : {}),
    ...(item.type ? { type: String(item.type) } : {}),
  };
};

const collect = (value) => {
  if (value === undefined || value === null || value === "") return [];
  if (Array.isArray(value)) return value.flatMap(collect);
  if (typeof value === "object") {
    if (Array.isArray(value.errors)) return collect(value.errors);
    return [value];
  }
  if (typeof value !== "string") return [];

  const str = value.trim().replace(/^(Error:\s*)+/i, "");
  try {
    const parsed = JSON.parse(str);
    if (parsed && typeof parsed === "object") return collect(parsed);
  } catch {
    /* not plain JSON, try the other shapes */
  }
  if (isPgArrayLiteral(str)) return parsePgArrayLiteral(str).flatMap(collect);
  return extractJsonObjects(str);
};

/**
 * Normalizes any backend representation of traceability errors/warnings
 * into a list of { msg, sheet?, row?, column?, type? }.
 */
export const parseTraceabilityIssues = (value) =>
  collect(value).map(toIssue).filter(Boolean);

/* ───────────────────────────────────────────────────────────────
 *  TABLE (same look as the Ecobalyse GTIN upload logs)
 * ──────────────────────────────────────────────────────────── */

const plural = (count, word) => `${count} ${word}${count === 1 ? "" : "s"}`;

// One line per issue, as received. Rows are strings so Excel keeps them as written.
export const traceabilityIssueRows = (issues) => [
  ["Sheet", "Row", "Column", "Type", "Message"],
  ...issues.map((issue) => [
    issue.sheet || "",
    issue.row || "",
    issue.column || "",
    issue.type || "",
    issue.msg,
  ]),
];

const SHEET_COLUMNS = [
  { wch: 20 },
  { wch: 8 },
  { wch: 10 },
  { wch: 34 },
  { wch: 90 },
];

export const downloadTraceabilityIssues = async (errors, warnings, fileName) => {
  const XLSX = await import("xlsx");
  const book = XLSX.utils.book_new();
  [
    ["Errors", errors],
    ["Warnings", warnings],
  ].forEach(([name, issues]) => {
    if (!issues.length) return;
    const sheet = XLSX.utils.aoa_to_sheet(traceabilityIssueRows(issues));
    sheet["!cols"] = SHEET_COLUMNS;
    XLSX.utils.book_append_sheet(book, sheet, name);
  });
  const buffer = XLSX.write(book, { bookType: "xlsx", type: "array" });
  const url = URL.createObjectURL(
    new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }),
  );
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
};

const detailsFileName = (uploadedName) =>
  `${(uploadedName || "traceability").replace(/\.[^.]+$/, "")}-upload-details.xlsx`;

// Keeps the page responsive with large files; the download has the complete list.
const MAX_ROWS_SHOWN = 200;

const IssuesTable = ({ issues, kind }) => {
  const [sheet, setSheet] = useState("ALL");
  const sheets = [...new Set(issues.map((i) => i.sheet).filter(Boolean))];
  const filtered =
    sheet === "ALL" ? issues : issues.filter((i) => i.sheet === sheet);
  const shown = filtered.slice(0, MAX_ROWS_SHOWN);
  const count = (key) =>
    key === "ALL" ? issues.length : issues.filter((i) => i.sheet === key).length;

  return (
    <div style={{ marginTop: 12 }}>
      {sheets.length > 1 && (
        <div
          role="group"
          aria-label={`Filter ${kind}s by sheet`}
          style={{ marginBottom: 8 }}
        >
          {["ALL", ...sheets].map((key) => (
            <Chip
              key={key}
              size="small"
              label={`${key === "ALL" ? "All" : key} (${count(key)})`}
              clickable
              color={sheet === key ? "primary" : "default"}
              aria-pressed={sheet === key}
              onClick={() => setSheet(key)}
              style={{ marginRight: 8, marginBottom: 4 }}
            />
          ))}
        </div>
      )}
      <div style={{ maxHeight: 400, overflow: "auto" }}>
        <Table size="small" aria-label={`Traceability ${kind}s`} stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Sheet</TableCell>
              <TableCell align="right">Row</TableCell>
              <TableCell>Column</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Message</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {shown.map((issue, index) => (
              <TableRow key={`${issue.sheet || ""}-${issue.row || ""}-${index}`}>
                <TableCell>{issue.sheet}</TableCell>
                <TableCell align="right">{issue.row}</TableCell>
                <TableCell>{issue.column}</TableCell>
                <TableCell>{issue.type}</TableCell>
                <TableCell style={{ wordBreak: "break-word" }}>
                  {issue.msg}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {filtered.length > shown.length && (
        <Typography variant="caption" color="textSecondary">
          Showing {shown.length} of {filtered.length} {kind}s. Download the
          details for the complete list.
        </Typography>
      )}
    </div>
  );
};

/**
 * Traceability errors and warnings, one table row per issue, with a summary
 * line, sheet filters and an .xlsx download. Used by File uploads and Project → Check.
 */
export const TraceabilityIssues = ({ errors = [], warnings = [], uploadedName }) => {
  const [downloading, setDownloading] = useState(false);
  if (!errors.length && !warnings.length) return null;

  const handleDownload = () => {
    setDownloading(true);
    downloadTraceabilityIssues(
      errors,
      warnings,
      detailsFileName(uploadedName),
    ).finally(() => setDownloading(false));
  };

  return (
    <div
      role="status"
      aria-label="Traceability upload result"
      style={{ marginTop: 16, width: "100%" }}
    >
      <Typography variant="body2">
        {[
          errors.length > 0 && plural(errors.length, "error"),
          warnings.length > 0 && plural(warnings.length, "warning"),
        ]
          .filter(Boolean)
          .join(" · ")}
      </Typography>
      <Button
        size="small"
        variant="outlined"
        startIcon={<GetAppIcon />}
        onClick={handleDownload}
        disabled={downloading}
        style={{ marginTop: 8 }}
      >
        {errors.length > 0 && warnings.length > 0
          ? "Download errors and warnings"
          : errors.length > 0
          ? "Download errors"
          : "Download warnings"}
      </Button>

      {errors.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <Typography variant="subtitle2" style={{ color: "#c62828" }}>
            Errors
          </Typography>
          <IssuesTable issues={errors} kind="error" />
        </div>
      )}
      {warnings.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <Typography variant="subtitle2" style={{ color: "#b26a00" }}>
            Warnings
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Warnings do not block the upload.
          </Typography>
          <IssuesTable issues={warnings} kind="warning" />
        </div>
      )}
    </div>
  );
};

/**
 * Upload history (File uploads) reading of a TRACEABILITY record:
 * errors from `stacktrace` (FAILED only) and warnings from `warnings`.
 * Null for other uploads or when nothing recognisable was stored.
 */
export const traceabilityResultFromHistory = (record) => {
  if (record?.tab !== "TRACEABILITY") return null;
  const errors =
    record.status === "FAILED" ? parseTraceabilityIssues(record.stacktrace) : [];
  const warnings = parseTraceabilityIssues(record.warnings);
  return errors.length || warnings.length ? { errors, warnings } : null;
};

/** Traceability view for the upload history detail page; null for other uploads. */
export const TraceabilityHistoryResult = ({ record }) => {
  const result = traceabilityResultFromHistory(record);
  if (!result) return null;
  return (
    <TraceabilityIssues
      errors={result.errors}
      warnings={result.warnings}
      uploadedName={record.filename}
    />
  );
};
